import type { Organization } from '@/types/ringcentral';

import { ringCentralCache } from '@/utils/ttl-cache';

import { ringCentralAPI } from './ringcentral-api';

// ============ RingCentral API Caching ============
// Uses manual TTL cache with 5-minute default

/**
 * Cached RingCentral organizations - 5 minutes TTL
 */
export async function fetchRingCentralOrganizations(options?: { force?: boolean }): Promise<{
  organizations: Organization[];
  count: number;
}> {
  const cacheKey = 'ringcentral:organizations';

  return ringCentralCache.getOrFetch(
    cacheKey,
    async () => {
      const result = await ringCentralAPI.getUserOrganizations();

      // Transform the API response to match the expected format
      return {
        organizations: result,
        count: result.length,
      };
    },
    { force: options?.force, ttl: 5 * 60 * 1000 }, // 5 minutes
  );
}

/**
 * Cached single RingCentral organization - 5 minutes TTL
 */
export async function fetchRingCentralOrganization(
  organizationId: string,
  options?: { force?: boolean },
): Promise<Organization> {
  const cacheKey = `ringcentral:org:${organizationId}`;

  return ringCentralCache.getOrFetch(
    cacheKey,
    async () => {
      return await ringCentralAPI.getOrganization(organizationId);
    },
    { force: options?.force, ttl: 5 * 60 * 1000 }, // 5 minutes
  );
}

/**
 * Clear cache for specific organization
 * Use this after updating organization data (e.g., staff list changes)
 */
export function clearOrganizationCache(organizationId: string): void {
  const cacheKey = `ringcentral:org:${organizationId}`;
  ringCentralCache.delete(cacheKey);
  console.log('🗑️ Cleared cache for organization:', organizationId);
}

/**
 * Clear all organizations list cache
 * Use this after creating a new organization
 */
export function clearOrganizationsListCache(): void {
  const cacheKey = 'ringcentral:organizations';
  ringCentralCache.delete(cacheKey);
  console.log('🗑️ Cleared organizations list cache');
}
